import type React from 'react';
import {
  type FC,
  type MouseEventHandler,
  type ReactElement,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import type { AriaProps } from '@iconik-io/shared-lib/src/react/a11y/aria';
import type { IconName } from '@iconik-io/uad';

import { errorMessage } from '../../ducks/messages/thunks';
import { useAppDispatch } from '../../store/react_redux_typed';
import type { ButtonVariant } from '../../ui/button/types';
import IconButton from '../../ui/icon_button/icon_button';
import TooltipTrigger from '../tooltip_trigger/tooltip_trigger';
import type { TooltipPlacement } from '../tooltip_trigger/utils';

import styles from './copy_button.module.css';

type Props = {
  className?: string;
  copy: string;
  text?: string | ReactElement;
  placement?: TooltipPlacement;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  variant?: ButtonVariant;
  iconName?: IconName;
  aria?: AriaProps;
};

const CopyButton: FC<Props> = (props) => {
  const timer = useRef<number>(undefined);
  const [iconName, setIconName] = useState<IconName>(props.iconName ?? 'copy');
  const [animationClassName, setAnimationClassName] = useState<string>();
  const dispatch = useAppDispatch();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    navigator.clipboard.writeText(props.copy).then(
      () => {
        setAnimationClassName(styles.tick_animation);
        setIconName('checkmark');
        if (timer.current) {
          clearTimeout(timer.current);
        }
        timer.current = window.setTimeout(() => {
          setIconName(props.iconName ?? 'copy');
          setAnimationClassName(styles.tick_reset_animation);
        }, 3000);
      },
      () => {
        dispatch(errorMessage({ text: 'Copy to clipboard failed' }));
      },
    );

    props.onClick?.(event);
  };

  const {
    text = 'Copy to clipboard',
    placement = 'left',
    className,
    variant,
  } = props;

  return (
    <TooltipTrigger
      placement={placement}
      tooltipText={text}
      component={IconButton}
      componentProps={{
        className: classNames(className, animationClassName),
        iconName,
        onClick: handleClick,
        variant,
        aria: props.aria,
      }}
    />
  );
};

export default CopyButton;
